import { useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleDot, X } from 'lucide-react';
import { Shot, getZoneFromShotType } from '../types/curling';
import { endTypeFromEndNumber, turnToDirection } from '../utils/teeLineEstimate';

interface ShotPlanViewProps {
  shot: Shot;
}

/**
 * Trigger button + modal replaying a shot's plan from the Shot Visualizer —
 * read-only, no dragging, no target-zone buttons, no Set. Everything it draws
 * comes straight from the shot's own saved fields (broomPosition, turn,
 * expectedCurl, expectedTime), the same fields every shot already carries, so
 * this adds no new positional data of its own — just a static replay of what
 * was already recorded.
 */
export function ShotPlanView({ shot }: ShotPlanViewProps) {
  const [show, setShow] = useState(false);

  const W = 340;
  const H = 560;
  const centerX = W / 2;
  const hogLineY = 40;
  const teeLineY = 340;
  const boardsY = H - 30;
  const PX_PER_FOOT = 20;
  const radii = {
    twelve: 6 * PX_PER_FOOT,
    eight: 4 * PX_PER_FOOT,
    four: 2 * PX_PER_FOOT,
    button: 0.5 * PX_PER_FOOT,
  };
  const backLineY = teeLineY + radii.twelve;
  const houseFrontY = teeLineY - radii.twelve;

  // Depth (y) for each of the 17 position zones, matching the Shot Visualizer's
  // own guard/draw spacing for 0-11, then continuing that same rhythm for the
  // hit-weight zones 12-16 (which have no button of their own, only a resting
  // depth once thrown) so every zone the server tracks has somewhere to draw.
  const ZONE_Y = [
    hogLineY - 15,
    hogLineY + (houseFrontY - hogLineY) * 0.25,
    hogLineY + (houseFrontY - hogLineY) * 0.5,
    hogLineY + (houseFrontY - hogLineY) * 0.75,
    houseFrontY,
    teeLineY - radii.eight,
    teeLineY - radii.four,
    teeLineY,
    teeLineY + radii.four,
    teeLineY + radii.eight,
    backLineY,
    backLineY + 7,
    backLineY + 17,
    backLineY + 27,
    backLineY + 37,
    backLineY + 47,
    backLineY + 57,
  ];
  const zoneY = (zone: number) => ZONE_Y[Math.min(Math.max(zone, 0), 16)];

  const endType = endTypeFromEndNumber(shot.endNumber);
  const direction = turnToDirection(shot.turn);
  const flip = endType === 'away';
  const Y = (y: number) => (flip ? H - y : y);

  const calledZone = getZoneFromShotType(shot.shotType);
  const broomRenderX = centerX + shot.broomPosition * PX_PER_FOOT;
  const broomRenderY = Y(zoneY(calledZone));
  const curlSign = direction === 'left-right' ? 1 : -1;
  const curlPx = Math.abs(shot.expectedCurl) * PX_PER_FOOT;
  const rockX = Math.min(Math.max(broomRenderX + curlSign * curlPx, 25), W - 25);

  // The actual delivery's own broom placement isn't recorded, only the curl
  // it produced and the zone it came to rest in — so the actual rock is
  // anchored off the same called broom x, offset by the actual curl instead
  // of the expected one, same sign convention as the called rock above.
  const actualRenderY = Y(zoneY(shot.actualPosition));
  const actualCurlPx = Math.abs(shot.curl) * PX_PER_FOOT;
  const actualRockX = Math.min(Math.max(broomRenderX + curlSign * actualCurlPx, 25), W - 25);

  return (
    <>
      <button
        type="button"
        onClick={() => setShow(true)}
        className="text-purple-600 hover:text-purple-800"
        title="View shot plan"
      >
        <CircleDot className="w-5 h-5" />
      </button>

      {show && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShow(false)}
        >
          <div
            className="bg-slate-900 border border-white/15 rounded-2xl shadow-2xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">Shot Plan</h2>
              <button onClick={() => setShow(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
              <rect x={20} y={20} width={W - 40} height={H - 40} rx={8} fill="#0f2942" stroke="rgba(255,255,255,0.15)" />

              <circle cx={centerX} cy={Y(teeLineY)} r={radii.twelve} fill="#2563eb" fillOpacity={0.25} stroke="#3b82f6" strokeWidth={1.5} />
              <circle cx={centerX} cy={Y(teeLineY)} r={radii.eight} fill="#0f2942" stroke="#e2e8f0" strokeWidth={1.5} />
              <circle cx={centerX} cy={Y(teeLineY)} r={radii.four} fill="#dc2626" fillOpacity={0.35} stroke="#ef4444" strokeWidth={1.5} />
              <circle cx={centerX} cy={Y(teeLineY)} r={radii.button} fill="#ef4444" />

              <line x1={20} y1={Y(hogLineY)} x2={W - 20} y2={Y(hogLineY)} stroke="#f87171" strokeWidth={2.5} />
              <text x={W - 24} y={Y(hogLineY) + 14} textAnchor="end" fontSize={9} fill="#f87171">Hog Line</text>

              <line x1={20} y1={Y(teeLineY)} x2={W - 20} y2={Y(teeLineY)} stroke="rgba(255,255,255,0.35)" strokeWidth={1.5} />
              <text x={24} y={Y(teeLineY) + 14} fontSize={9} fill="rgba(255,255,255,0.5)">Tee</text>

              <line x1={20} y1={Y(backLineY)} x2={W - 20} y2={Y(backLineY)} stroke="rgba(255,255,255,0.35)" strokeWidth={1.5} />
              <text x={24} y={Y(backLineY) + 14} fontSize={9} fill="rgba(255,255,255,0.5)">Back Line</text>

              <line x1={20} y1={Y(boardsY)} x2={W - 20} y2={Y(boardsY)} stroke="#94a3b8" strokeWidth={4} />
              <text
                x={centerX}
                y={Y(boardsY) + (Y(teeLineY) > Y(boardsY) ? 16 : -10)}
                textAnchor="middle"
                fontSize={9}
                fill="#94a3b8"
              >
                Back Boards
              </text>

              <line x1={centerX} y1={Y(hogLineY)} x2={centerX} y2={Y(backLineY)} stroke="rgba(255,255,255,0.3)" strokeWidth={1.5} strokeDasharray="4 3" />

              {/* Dotted link from called to actual, when they differ */}
              {(actualRockX !== rockX || actualRenderY !== broomRenderY) && (
                <line
                  x1={rockX} y1={broomRenderY} x2={actualRockX} y2={actualRenderY}
                  stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="3 3" opacity={0.7}
                />
              )}

              {/* Called rock */}
              <circle cx={rockX} cy={broomRenderY} r={11} fill="#ffffff" stroke="#ec4899" strokeWidth={3} />
              <circle cx={rockX} cy={broomRenderY} r={3} fill="#ec4899" />

              {/* Called broom */}
              <rect x={broomRenderX - 3} y={broomRenderY - 12} width={6} height={24} rx={2} fill="#facc15" stroke="#78350f" strokeWidth={1} />

              {/* Turn label above the broom */}
              <text
                x={broomRenderX}
                y={Math.max(broomRenderY - 20, 30)}
                textAnchor="middle"
                fontSize={10}
                fontWeight="bold"
                fill={shot.turn === 'clockwise' ? '#ef4444' : '#3b82f6'}
              >
                {shot.turn === 'clockwise' ? 'CW' : 'CCW'}
              </text>

              {/* Actual outcome: smaller hollow amber ring nested inside the called
                  rock's radius, so a spot-on shot still shows both — the pink ring
                  peeking out around the amber one — instead of one fully hiding
                  the other. */}
              <circle cx={actualRockX} cy={actualRenderY} r={7} fill="#f59e0b" fillOpacity={0.25} stroke="#f59e0b" strokeWidth={2.5} />
            </svg>

            <div className="flex items-center gap-4 mt-1 text-xs">
              <span className="flex items-center gap-1.5 text-slate-400">
                <span className="w-2.5 h-2.5 rounded-full bg-white border-2 border-pink-500 inline-block" /> Called
              </span>
              <span className="flex items-center gap-1.5 text-slate-400">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500/30 border-2 border-amber-500 inline-block" /> Actual
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-2 flex items-center justify-between">
                <p className="text-xs text-slate-400">Called Weight</p>
                <p className="text-lg font-bold text-white">{shot.expectedTime.toFixed(1)}s</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-2 flex items-center justify-between">
                <p className="text-xs text-slate-400">Called Curl</p>
                <p className="text-lg font-bold text-white">{shot.expectedCurl.toFixed(1)} ft</p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-2 flex items-center justify-between">
                <p className="text-xs text-amber-400">Actual Weight</p>
                <p className="text-lg font-bold text-white">{shot.time.toFixed(1)}s</p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-2 flex items-center justify-between">
                <p className="text-xs text-amber-400">Actual Curl</p>
                <p className="text-lg font-bold text-white">{shot.curl.toFixed(1)} ft</p>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
